import React, { useCallback, useEffect, useState } from 'react';
import type { TimeSignature } from '../../lib/rhythm/ecoRitmoConfig';
import { presetKey, useEcoLayoutTuning } from '../../contexts/EcoLayoutTuningContext';
import {
  exportSlotsForConfig,
  presetLabel,
  tuningExportJson,
  type EcoLayoutPreset,
  type EcoPresetKey,
  type EcoSlotTuning,
} from '../../lib/rhythm/ecoLayoutTuning';
import { TRAIN_SPRITES } from '../../lib/rhythm/trainSprites';

interface EcoLayoutTunerProps {
  timeSignature: TimeSignature;
  measureCount: number;
  open: boolean;
  onClose: () => void;
}

function SliderRow({
  label,
  value,
  min,
  max,
  step,
  unit = '',
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit?: string;
  onChange: (v: number) => void;
}) {
  return (
    <label className="eco-tuner-row">
      <span className="eco-tuner-row-label">
        {label}{' '}
        <strong>
          {value}
          {unit}
        </strong>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="eco-tuner-slider"
      />
    </label>
  );
}

function LayoutSliders({
  preset,
  onChange,
}: {
  preset: EcoLayoutPreset;
  onChange: (patch: Partial<EcoLayoutPreset>) => void;
}) {
  return (
    <>
      <SliderRow
        label="Escala"
        value={preset.trainScale}
        min={0.4}
        max={2}
        step={0.05}
        unit="×"
        onChange={(v) => onChange({ trainScale: v })}
      />
      <SliderRow
        label="Altura"
        value={preset.trainHeightMult}
        min={0.5}
        max={1.5}
        step={0.05}
        unit="×"
        onChange={(v) => onChange({ trainHeightMult: v })}
      />
      <SliderRow
        label="Gap filas"
        value={preset.rowGapRem}
        min={0}
        max={2}
        step={0.05}
        unit="rem"
        onChange={(v) => onChange({ rowGapRem: v })}
      />
      <SliderRow
        label="Gap cols"
        value={preset.colGapRem}
        min={0}
        max={2}
        step={0.05}
        unit="rem"
        onChange={(v) => onChange({ colGapRem: v })}
      />
      <SliderRow
        label="Max 1 fila"
        value={preset.singleRowMaxPx}
        min={400}
        max={1200}
        step={10}
        unit="px"
        onChange={(v) => onChange({ singleRowMaxPx: v })}
      />
      <SliderRow
        label="Figuras"
        value={preset.figureScale}
        min={0.8}
        max={2.2}
        step={0.05}
        unit="×"
        onChange={(v) => onChange({ figureScale: v })}
      />
    </>
  );
}

function SlotSliders({
  ts,
  slot,
  onChange,
}: {
  ts: TimeSignature;
  slot: EcoSlotTuning;
  onChange: (patch: Partial<EcoSlotTuning>) => void;
}) {
  return (
    <div className="eco-tuner-section">
      <p className="eco-tuner-section-title">Ventanas del sprite ({ts})</p>
      <SliderRow
        label="Top"
        value={slot.offsetTop}
        min={-20}
        max={20}
        step={0.5}
        unit="%"
        onChange={(v) => onChange({ offsetTop: v })}
      />
      <SliderRow
        label="Left"
        value={slot.offsetLeft}
        min={-20}
        max={20}
        step={0.5}
        unit="%"
        onChange={(v) => onChange({ offsetLeft: v })}
      />
      <SliderRow
        label="Ancho"
        value={slot.widthScale}
        min={0.5}
        max={1.5}
        step={0.02}
        unit="×"
        onChange={(v) => onChange({ widthScale: v })}
      />
      <SliderRow
        label="Alto"
        value={slot.heightScale}
        min={0.5}
        max={1.5}
        step={0.02}
        unit="×"
        onChange={(v) => onChange({ heightScale: v })}
      />
    </div>
  );
}

export const EcoLayoutTuner: React.FC<EcoLayoutTunerProps> = ({
  timeSignature,
  measureCount,
  open,
  onClose,
}) => {
  const { store, updatePreset, updateSlots, resetAll, persist, presetKeys } =
    useEcoLayoutTuning();
  const liveKey = presetKey(timeSignature, measureCount);
  const [editKey, setEditKey] = useState<EcoPresetKey>(liveKey);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setEditKey(liveKey);
  }, [liveKey]);

  const editTs = editKey.split('@')[0] as TimeSignature;
  const preset = store.presets[editKey];
  const slot = store.slots[editTs];
  const isLive = editKey === liveKey;

  const copyConfig = useCallback(async () => {
    const base = TRAIN_SPRITES[editTs]?.slots ?? [];
    const payload = {
      presetKey: editKey,
      layout: preset,
      slots: store.slots[editTs],
      slotsResolved: exportSlotsForConfig(base, store.slots[editTs]),
      fullStore: store,
    };
    const text = JSON.stringify(payload, null, 2);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      console.log('Eco layout export:\n', text);
    }
  }, [editKey, editTs, preset, store]);

  if (!open) return null;

  return (
    <aside className="eco-tuner" aria-label="Ajuste de layout">
      <div className="eco-tuner-header">
        <span className="eco-tuner-title">Ajuste layout</span>
        <button type="button" className="eco-tuner-close" onClick={onClose} aria-label="Cerrar">
          ×
        </button>
      </div>

      <div className="eco-tuner-body eco-scroll-hidden">
        <div className="eco-tuner-section">
          <p className="eco-tuner-section-title">Escenario</p>
          <p className="eco-tuner-live">
            En pantalla: <strong>{presetLabel(liveKey)}</strong>
            {!isLive && (
              <span className="eco-tuner-live-note"> (editando otro preset)</span>
            )}
          </p>
          <div className="eco-tuner-preset-grid">
            {presetKeys.map((key) => {
              const active = key === editKey;
              const isCurrent = key === liveKey;
              return (
                <button
                  key={key}
                  type="button"
                  className={`eco-tuner-preset-chip ${active ? 'eco-tuner-preset-chip--active' : ''} ${isCurrent ? 'eco-tuner-preset-chip--live' : ''}`}
                  onClick={() => setEditKey(key)}
                >
                  {key.replace('@', ' · ')}
                </button>
              );
            })}
          </div>
        </div>

        <div className="eco-tuner-section">
          <p className="eco-tuner-section-title">Tren — {presetLabel(editKey)}</p>
          <LayoutSliders
            preset={preset}
            onChange={(patch) => updatePreset(editKey, patch)}
          />
        </div>

        <SlotSliders
          ts={editTs}
          slot={slot}
          onChange={(patch) => updateSlots(editTs, patch)}
        />

        <p className="eco-tuner-hint">
          Calibra cada combinación: compás musical (2/4, 3/4, 4/4) × cuántos compases hay (+/−
          arriba). Las ventanas del sprite son por compás; el tamaño del tren es por escenario.
        </p>
      </div>

      <div className="eco-tuner-footer">
        <button type="button" className="eco-tuner-btn" onClick={resetAll}>
          Reset todo
        </button>
        <button type="button" className="eco-tuner-btn eco-tuner-btn--primary" onClick={persist}>
          Guardar
        </button>
        <button type="button" className="eco-tuner-btn" onClick={copyConfig}>
          {copied ? 'Copiado' : 'Copiar JSON'}
        </button>
      </div>

      <details className="eco-tuner-export">
        <summary>Ver JSON completo (12 presets)</summary>
        <pre className="eco-tuner-pre">{tuningExportJson(store)}</pre>
      </details>
    </aside>
  );
};

export default EcoLayoutTuner;
